// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { supabase } from './config/supabase';
import { initApiClient } from './lib/apiClient';
import Login from './components/Auth/Login';
import AuthCallback from './components/Auth/AuthCallback';
import EmailVerification from './components/Auth/EmailVerification';
import ResetPassword from './components/Auth/ResetPassword';
import PendingApproval from './components/Auth/PendingApproval';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import LandingPage from './components/LandingPage/LandingPage';
import TermsOfService from './components/Legal/TermsOfService';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import InviteAccept from './components/InviteAccept/InviteAccept';
import MainApp from './MainApp';
import SandboxRoot from './sandbox';


// Swaps the whole app for the "awaiting approval" screen the first time any
// request comes back ACCOUNT_PENDING. Lives inside AuthProvider (the screen
// needs user/signOut) and wraps the router rather than being a route, because
// the native shell cannot hard-navigate to a route path — there is no server
// behind capacitor://localhost.
function PendingGate({ children }) {
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const onPending = () => setPending(true);
    window.addEventListener('tekstak:account-pending', onPending);
    return () => window.removeEventListener('tekstak:account-pending', onPending);
  }, []);

  return pending ? <PendingApproval /> : children;
}

function App() {
  // Initialize apiClient with Supabase as soon as App mounts.
  //
  // On localhost we want EMPTY baseUrl so requests use relative paths
  // and are intercepted by CRA's setupProxy.js (no CORS dance). The
  // REACT_APP_API_URL env var is honored only when running on a real
  // host (production / Vercel preview) — locally it's ignored even
  // when set, because the proxy is the right path.
  useEffect(() => {
    // No baseUrl: apiClient already defaults to platform/API_BASE, which is
    // the single resolver. Passing a second opinion from here is how the
    // codebase ended up with six of them.
    initApiClient({ supabase });
  }, []);

  return (
    <Router>
      <AuthProvider>
        <PendingGate>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login initialMode="signup" />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          {/* Destination of the password-reset email. Without this route the
              recovery link authenticated the user and then fell through to the
              catch-all, leaving no way to actually set a password. */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/sandbox/*" element={<SandboxRoot />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            }
          />
        </Routes>
        </PendingGate>
      </AuthProvider>
    </Router>
  );
}

export default App;